/**
 * useHeartbeat — POST /admin-monitoring/presence/heartbeat/ every 30s.
 *
 * Multi-tab dedup: only the "leader" tab fires the heartbeat. Leadership is
 * decided via BroadcastChannel + a localStorage timestamp lock; falls back to
 * "always fire" if BroadcastChannel is unavailable.
 *
 * Pauses when document.hidden (Page Visibility API).
 */
import { useEffect, useRef } from 'react';
import api from '../api/api';

const HEARTBEAT_INTERVAL_MS = 30_000;
const LOCK_KEY = 'cxhub_presence_leader';
const LOCK_TTL_MS = 45_000; // leader must refresh inside this window

function tryAcquireLock(tabId) {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    const now = Date.now();
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.tabId && parsed.tabId !== tabId && (now - parsed.ts) < LOCK_TTL_MS) {
        return false;
      }
    }
    localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId, ts: now }));
    return true;
  } catch {
    return true; // best effort
  }
}

function refreshLock(tabId) {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || parsed.tabId === tabId) {
      localStorage.setItem(LOCK_KEY, JSON.stringify({ tabId, ts: Date.now() }));
    }
  } catch { /* noop */ }
}

export const useHeartbeat = ({ enabled, getStatus, getCurrentModule }) => {
  const tabIdRef = useRef(Math.random().toString(36).slice(2));
  const timerRef = useRef(null);
  // Keep latest getters in refs so the heartbeat effect only re-binds when
  // `enabled` flips. Without this, every parent re-render produced new arrow
  // identities, the effect cleaned up + immediately fired again — turning a
  // 30s heartbeat into a per-render request storm.
  const getStatusRef = useRef(getStatus);
  const getCurrentModuleRef = useRef(getCurrentModule);
  useEffect(() => { getStatusRef.current = getStatus; }, [getStatus]);
  useEffect(() => { getCurrentModuleRef.current = getCurrentModule; }, [getCurrentModule]);

  useEffect(() => {
    if (!enabled) return undefined;
    const tabId = tabIdRef.current;

    const fire = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (!tryAcquireLock(tabId)) return;
      const gs = getStatusRef.current;
      const gm = getCurrentModuleRef.current;
      try {
        await api.post('admin-monitoring/presence/heartbeat/', {
          status: (typeof gs === 'function' ? gs() : 'online') || 'online',
          current_module: (typeof gm === 'function' ? gm() : '') || '',
        });
        refreshLock(tabId);
      } catch {
        // Network errors are silent — next tick will retry
      }
    };

    // Fire immediately, then on interval
    fire();
    timerRef.current = setInterval(fire, HEARTBEAT_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (!document.hidden) fire();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);
};

export default useHeartbeat;
